import { useCallback, useMemo } from "react";

import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { usePinnedFiltersMutation } from "../query/mutations/usePinnedFiltersMutation";
import { useFiltersSelectionQuery } from "../query/queries/useFiltersSelectionQuery";
import pinService from "../services/pin-service";
import advancedSearchFilteringTrackingService from "../services/tracking";
import type { IFiltersSelectionDto } from "../types/dto";
import type { SearchFilterInstance } from "../types/searchFilterInstance";
import { pickChangedKeys } from "../utils/filterKeys";

const DEFAULT_SELECTED_FILTER_KEYS: ReadonlySet<string> = new Set();

type PinnedFiltersParams = {
    readonly filtersConfig: readonly SearchFilterInstance[];
};

type PinnedFiltersValue = {
    readonly selectedFilterKeys: ReadonlySet<string>;
    readonly nonPinnableFilterKeys: ReadonlySet<string>;
    readonly isPinDisabled: boolean;

    checkIsFilterPinned: (filterKey: string) => boolean;
    onPinSingleFilter: (filterKey: string) => void;
    onPinChangedFilters: <T extends IPlainObject>(previousValue: Partial<T>, nextValue: Partial<T>) => void;
};

export const usePinnedFilters = ({ filtersConfig }: PinnedFiltersParams): PinnedFiltersValue => {
    const selectFilterKeys = useCallback(
        ({ filters }: IFiltersSelectionDto): ReadonlySet<string> => new Set(filters),
        [],
    );
    const { data: selectedFilterKeys = DEFAULT_SELECTED_FILTER_KEYS, isLoading } = useFiltersSelectionQuery({
        select: selectFilterKeys,
    });

    const { mutate: mutateFiltersSelection } = usePinnedFiltersMutation();

    const nonPinnableFilterKeys = useMemo(
        () => new Set(filtersConfig.filter((f) => f.visibility).map((f) => f.key)),
        [filtersConfig],
    );

    const isPinDisabled = isLoading;

    const pinFilters = useCallback(
        (filterNames: readonly string[]) => {
            const newFilters = filterNames.filter(
                (key) => !selectedFilterKeys.has(key) && !nonPinnableFilterKeys.has(key),
            );

            if (!newFilters.length) {
                return;
            }

            advancedSearchFilteringTrackingService.filtersPanel.trackPinFilter(newFilters[0], true);

            mutateFiltersSelection([...selectedFilterKeys, ...newFilters]);
        },
        [mutateFiltersSelection, selectedFilterKeys, nonPinnableFilterKeys],
    );

    const onPinChangedFilters = useCallback(
        <T extends IPlainObject>(previousValue: Partial<T>, nextValue: Partial<T>): void => {
            const changedKeys = pickChangedKeys<T>(previousValue, nextValue);

            if (changedKeys.length === 0) {
                return;
            }

            const pinKeys = changedKeys.flatMap((key) => pinService.resolveInstancePinKeys(filtersConfig, String(key)));

            pinFilters(pinKeys);
        },
        [pinFilters, filtersConfig],
    );

    const onUnpinFilters = useCallback(
        (filterNames: readonly string[]) => {
            const toRemove = new Set(filterNames);
            const next = [...selectedFilterKeys].filter((name) => !toRemove.has(name));

            if (next.length === selectedFilterKeys.size) {
                return;
            }

            advancedSearchFilteringTrackingService.filtersPanel.trackPinFilter(filterNames[0], false);

            mutateFiltersSelection(next);
        },
        [mutateFiltersSelection, selectedFilterKeys],
    );

    const onPinSingleFilter = useCallback(
        (filterKey: string) => {
            if (nonPinnableFilterKeys.has(filterKey)) {
                return;
            }

            const pinKeys = pinService.resolveInstancePinKeys(filtersConfig, filterKey);

            if (pinService.isInstancePinnedByKey(filtersConfig, filterKey, selectedFilterKeys)) {
                onUnpinFilters(pinKeys);
            } else {
                pinFilters(pinKeys);
            }
        },
        [selectedFilterKeys, nonPinnableFilterKeys, onUnpinFilters, pinFilters, filtersConfig],
    );

    const checkIsFilterPinned = useCallback(
        (filterKey: string) => pinService.isInstancePinnedByKey(filtersConfig, filterKey, selectedFilterKeys),
        [filtersConfig, selectedFilterKeys],
    );

    return {
        selectedFilterKeys,
        nonPinnableFilterKeys,
        isPinDisabled,
        checkIsFilterPinned,
        onPinSingleFilter,
        onPinChangedFilters,
    };
};
